import { css, styled } from "styled-components";
import { focusVisible } from "../lib/ui";

/**
 * Text pill buttons replacing the old word-art SVG pills (Done/Create/OK/
 * Cancel). Real text instead of images of text (WCAG 1.4.9), localizable,
 * and the fills carry small white text so they use the AAA-safe colours
 * from the style guide. Hover states mirror the old SVG art: fills invert
 * to outlines, the danger outline fills in.
 */

const variants = {
	primary: css`
        background: #5335c9; /* white on it: 7.7:1 (brand #8265FD was 4.1:1) */
        border: 1.5px solid #5335c9;
        color: #fff;

        &:hover {
            background: ${(props) => props.theme.backgroundColor};
            color: #5335c9;
        }
    `,
	dark: css`
        background: #111111;
        border: 1.5px solid #111111;
        color: #fff;

        &:hover {
            background: ${(props) => props.theme.backgroundColor};
            color: #111111;
        }
    `,
	primaryOutline: css`
        background: transparent;
        border: 1.5px solid #5335c9;
        color: #5335c9; /* 7.7:1 on white */

        &:hover {
            background: #5335c9;
            color: #fff;
        }
    `,
	danger: css`
        background: transparent;
        border: 1.5px solid #992f29; /* 7.5:1 as text/border on white */
        color: #992f29;

        &:hover {
            background: #992f29;
            color: #fff;
        }
    `,
};

// default to type="button" but let callers pass type="submit" (static attrs
// would silently override the prop — that broke the image-search Search button)
export const StyledPillButton = styled.button.attrs<{ type?: string }>(
	(props) => ({ type: props.type ?? "button" }),
)<{
	$variant?: keyof typeof variants;
}>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    min-width: 48px;
    padding: 0 1.1em;
    border-radius: 8px;
    font-family: ${(props) => props.theme.font};
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    ${focusVisible}

    ${({ $variant }) => variants[$variant ?? "primary"]}

    &:disabled {
        background: #e6e6e6;
        border-color: #e6e6e6;
        color: #4f4f4f;
        cursor: not-allowed;
    }
`;
